import type React from "react";
import { useEffect, useState } from "react";
import {
  Box,
  Button,
  Divider,
  Drawer,
  LinearProgress,
  Slider,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import WorkspacePremiumOutlinedIcon from "@mui/icons-material/WorkspacePremiumOutlined";
import { tokens } from "../../../../theme";
import IconButton from "../../../../components/ui/IconButton";
import Chip from "../../../../components/ui/Chip";
import { STATUS_STYLES } from "../../constants";
import { reviewCompetencyAnswer } from "../../services/jobsStorage";
import type { ApplicationSubmission, CompetencyAnswer, PersonalityTraitScore } from "../../types";

interface CandidateDetailModalProps {
  submission: ApplicationSubmission | null;
  isOpen: boolean;
  onClose: () => void;
  onUpdateStatus: (submissionId: string, status: ApplicationSubmission["status"]) => void;
}

const TRAIT_ROWS: {
  key: keyof PersonalityTraitScore;
  left: string;
  right: string;
  color: string;
}[] = [
  { key: "mind", left: "Extraverted", right: "Introverted", color: "rgba(124,58,237,1)" },
  { key: "energy", left: "Intuitive", right: "Observant", color: "rgba(0,122,255,1)" },
  { key: "nature", left: "Thinking", right: "Feeling", color: "rgba(52,199,89,1)" },
  { key: "tactics", left: "Judging", right: "Prospecting", color: "rgba(255,149,0,1)" },
];

const SectionLabel = ({ children }: { children: React.ReactNode }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  return (
    <Typography
      sx={{
        fontWeight: 700,
        fontSize: 12,
        color: colors.primary[700],
        letterSpacing: "1px",
        textTransform: "uppercase",
        mb: 1.5,
      }}
    >
      {children}
    </Typography>
  );
};

const CandidateDetailModal: React.FC<CandidateDetailModalProps> = ({
  submission,
  isOpen,
  onClose,
  onUpdateStatus,
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const [reviewAnswers, setReviewAnswers] = useState<Record<string, CompetencyAnswer>>({});

  useEffect(() => {
    if (!submission) {
      setReviewAnswers({});
      return;
    }
    setReviewAnswers(
      Object.fromEntries(
        submission.competencyAnswers
          .filter((answer) => answer.answerId)
          .map((answer) => [answer.answerId as string, answer]),
      ),
    );
  }, [submission]);

  if (!submission) return null;
  const { candidateInfo, personalityResult, videoPitch, status } = submission;
  const displayedCompetencyScore = Object.values(reviewAnswers).reduce(
    (total, answer) => total + answer.scoreEarned,
    0,
  );
  const displayedOverallScore = submission.competencyMaxScore
    ? Math.round((displayedCompetencyScore / submission.competencyMaxScore) * 15)
    : 0;

  const saveAnswerReview = async (answer: CompetencyAnswer) => {
    if (!answer.answerId || answer.reviewPercentage === null || answer.reviewPercentage === undefined) return;
    const saved = await reviewCompetencyAnswer(
      submission.id,
      answer.answerId,
      answer.reviewPercentage,
      answer.reviewFeedback ?? "",
    );
    setReviewAnswers((current) => ({
      ...current,
      [answer.answerId as string]: {
        ...answer,
        scoreEarned: saved.score_awarded,
        reviewPercentage: saved.review_percentage,
        reviewFeedback: saved.review_feedback,
        reviewPending: false,
      },
    }));
  };

  return (
    <Drawer anchor="right" open={isOpen} onClose={onClose}>
      <Box
        sx={{
          width: { xs: "100vw", sm: 460 },
          height: "100dvh",
          boxSizing: "border-box",
          overflowY: "auto",
          overflowX: "hidden",
          p: 2.5,
          backgroundColor: colors.primary[900],
        }}
      >
        <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", mb: 2 }}>
          <Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography variant="h6" fontWeight={800} sx={{ color: colors.primary[100] }}>
                {candidateInfo.fullName}
              </Typography>
              <Chip
                status={STATUS_STYLES[status].status}
                label={STATUS_STYLES[status].label}
                sx={{ height: 22 }}
              />
            </Box>
            <Typography variant="caption" sx={{ color: colors.primary[700] }}>
              Applied for {submission.jobTitle} • Submitted on{" "}
              {new Date(submission.submittedAt).toLocaleDateString()}
            </Typography>
          </Box>
          <IconButton onClick={onClose} size="small" tooltip="Close">
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        <Divider sx={{ borderColor: colors.primary[300], mb: 2.5 }} />

        {/* Contact + status */}
        <Box sx={{ mb: 3 }}>
          <SectionLabel>Contact</SectionLabel>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mb: 2 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
              <MailOutlineIcon sx={{ fontSize: 16, color: colors.primary[400] }} />
              <Typography variant="body2" sx={{ color: colors.primary[100] }}>
                {candidateInfo.email}
              </Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
              <PhoneOutlinedIcon sx={{ fontSize: 16, color: colors.primary[400] }} />
              <Typography variant="body2" sx={{ color: colors.primary[100] }}>
                {candidateInfo.phone}
              </Typography>
            </Box>
            {candidateInfo.linkedInUrl && (
              <Box
                component="a"
                href={candidateInfo.linkedInUrl}
                target="_blank"
                rel="noreferrer"
                sx={{ display: "flex", alignItems: "center", gap: 0.5, color: colors.primary[400], fontWeight: 700, fontSize: 13, textDecoration: "none" }}
              >
                <LinkedInIcon sx={{ fontSize: 14 }} />
                LinkedIn Profile
              </Box>
            )}
            {candidateInfo.resumeUrl && (
              <Box
                component="a"
                href={candidateInfo.resumeUrl}
                target="_blank"
                rel="noreferrer"
                sx={{ display: "flex", alignItems: "center", gap: 0.5, color: colors.primary[400], fontWeight: 700, fontSize: 13, textDecoration: "none" }}
              >
                <OpenInNewIcon sx={{ fontSize: 14 }} />
                View Resume
              </Box>
            )}
          </Box>

          <Box sx={{ display: "flex", gap: 1 }}>
            <Button
              size="small"
              variant={status === "shortlisted" ? "contained" : "outlined"}
              startIcon={<CheckCircleOutlineIcon sx={{ fontSize: 16 }} />}
              onClick={() =>
                onUpdateStatus(submission.id, status === "shortlisted" ? "under_review" : "shortlisted")
              }
              sx={{ textTransform: "none", fontWeight: 700, borderRadius: "999px", boxShadow: "none" }}
              color="success"
            >
              {status === "shortlisted" ? "Shortlisted" : "Shortlist"}
            </Button>
            <Button
              size="small"
              variant={status === "rejected" ? "contained" : "outlined"}
              startIcon={<HighlightOffIcon sx={{ fontSize: 16 }} />}
              onClick={() =>
                onUpdateStatus(submission.id, status === "rejected" ? "under_review" : "rejected")
              }
              sx={{ textTransform: "none", fontWeight: 700, borderRadius: "999px", boxShadow: "none" }}
              color="error"
            >
              {status === "rejected" ? "Rejected" : "Reject"}
            </Button>
            <Button
              size="small"
              variant={status === "hired" ? "contained" : "outlined"}
              startIcon={<WorkspacePremiumOutlinedIcon sx={{ fontSize: 16 }} />}
              onClick={() => {
                if (status === "hired") {
                  onUpdateStatus(submission.id, "under_review");
                  return;
                }
                if (
                  window.confirm(
                    `Hire ${candidateInfo.fullName}? This creates a staff account for them (email: ${candidateInfo.email}).`,
                  )
                ) {
                  onUpdateStatus(submission.id, "hired");
                }
              }}
              sx={{ textTransform: "none", fontWeight: 700, borderRadius: "999px", boxShadow: "none" }}
              color="primary"
            >
              {status === "hired" ? "Hired" : "Hire"}
            </Button>
          </Box>
        </Box>

        {personalityResult && (
          <>
            <Divider sx={{ borderColor: colors.primary[300], mb: 2.5 }} />

            {/* Personality */}
            <Box sx={{ mb: 3 }}>
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1.5, flexWrap: "wrap", gap: 1 }}>
                <SectionLabel>16Personalities Profile</SectionLabel>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  {personalityResult.fileUrl && (
                    <Box
                      component="a"
                      href={personalityResult.fileUrl}
                      target="_blank"
                      rel="noreferrer"
                      sx={{ textDecoration: "none" }}
                    >
                      <Chip
                        status="success"
                        label="📄 View Screenshot"
                        sx={{
                          height: 22,
                          backgroundColor: "rgba(52,199,89,0.12)",
                          color: "rgba(52,199,89,1)",
                          fontWeight: 700,
                          cursor: "pointer",
                          "&:hover": { opacity: 0.85 },
                        }}
                      />
                    </Box>
                  )}
                  {personalityResult.profileUrl ? (
                    <Box
                      component="a"
                      href={personalityResult.profileUrl}
                      target="_blank"
                      rel="noreferrer"
                      sx={{ textDecoration: "none" }}
                    >
                      <Chip
                        status="info"
                        label={`${personalityResult.code} — ${personalityResult.title} ↗`}
                        sx={{ height: 22, backgroundColor: "rgba(124,58,237,0.1)", color: "rgba(124,58,237,1)", fontWeight: 700, cursor: "pointer", "&:hover": { opacity: 0.85 } }}
                      />
                    </Box>
                  ) : (
                    <Chip
                      status="info"
                      label={`${personalityResult.code} — ${personalityResult.title}`}
                      sx={{ height: 22, backgroundColor: "rgba(124,58,237,0.1)", color: "rgba(124,58,237,1)", fontWeight: 700 }}
                    />
                  )}
                </Box>
              </Box>
              <Typography variant="caption" sx={{ color: colors.primary[700], display: "block", mb: 2 }}>
                {personalityResult.description}
              </Typography>

              <Box sx={{ display: "flex", flexDirection: "column", gap: 1.75 }}>
                {TRAIT_ROWS.map((trait) => {
                  const value = personalityResult.scores?.[trait.key] ?? 50;
                  return (
                    <Box key={trait.key}>
                      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
                        <Typography variant="caption" fontWeight={700} sx={{ color: colors.primary[100] }}>
                          {trait.left} ({value}%)
                        </Typography>
                        <Typography variant="caption" fontWeight={700} sx={{ color: colors.primary[100] }}>
                          {trait.right} ({100 - value}%)
                        </Typography>
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={value}
                        sx={{
                          height: 8,
                          borderRadius: 4,
                          backgroundColor: colors.primary[800],
                          "& .MuiLinearProgress-bar": { backgroundColor: trait.color, borderRadius: 4 },
                        }}
                      />
                    </Box>
                  );
                })}
              </Box>
            </Box>
          </>
        )}

        <Divider sx={{ borderColor: colors.primary[300], mb: 2.5 }} />

        {/* Competency */}
        <Box sx={{ mb: videoPitch?.videoUrl ? 3 : 0 }}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1.5 }}>
            <SectionLabel>Competency Test</SectionLabel>
            <Typography variant="body2" fontWeight={800} sx={{ color: "rgba(52,199,89,1)" }}>
              {displayedCompetencyScore} / {submission.competencyMaxScore} pts ({displayedOverallScore}%)
            </Typography>
          </Box>

          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
            {submission.competencyAnswers.map((originalAnswer, idx) => {
              const answer = (originalAnswer.answerId && reviewAnswers[originalAnswer.answerId]) || originalAnswer;
              return (
              <Box
                key={answer.answerId ?? idx}
                sx={{ borderRadius: "10px", backgroundColor: colors.primary[800], p: 1.5 }}
              >
                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                  <Typography variant="caption" fontWeight={700} sx={{ color: colors.primary[100] }}>
                    Question {idx + 1}
                  </Typography>
                  <Typography variant="caption" fontWeight={700} sx={{ color: colors.primary[400] }}>
                    {answer.reviewPending ? "Review Pending" : `Score Earned: ${answer.scoreEarned} pts`}
                  </Typography>
                </Box>
                {answer.questionText && (
                  <Typography variant="body2" sx={{ mt: 1, color: colors.primary[100] }}>
                    {answer.questionText}
                  </Typography>
                )}
                {answer.openTextAnswer && (
                  <Typography
                    variant="caption"
                    sx={{ display: "block", mt: 1, fontStyle: "italic", color: colors.primary[700] }}
                  >
                    &ldquo;{answer.openTextAnswer}&rdquo;
                  </Typography>
                )}
                {answer.openTextAnswer && answer.answerId && (
                  <Box sx={{ mt: 1.5 }}>
                    <Typography variant="caption" sx={{ color: colors.primary[100] }}>
                      Review: {answer.reviewPercentage ?? 0}%
                    </Typography>
                    <Slider
                      value={answer.reviewPercentage ?? 0}
                      min={0}
                      max={100}
                      valueLabelDisplay="auto"
                      onChange={(_, value) => {
                        const percentage = Array.isArray(value) ? value[0] : value;
                        setReviewAnswers((current) => ({
                          ...current,
                          [answer.answerId as string]: {
                            ...answer,
                            reviewPercentage: percentage,
                            reviewPending: false,
                          },
                        }));
                      }}
                      sx={{ color: colors.primary[400] }}
                    />
                    <TextField
                      fullWidth
                      size="small"
                      multiline
                      minRows={2}
                      value={answer.reviewFeedback ?? ""}
                      onChange={(event) => setReviewAnswers((current) => ({
                        ...current,
                        [answer.answerId as string]: {
                          ...answer,
                          reviewFeedback: event.target.value,
                        },
                      }))}
                      placeholder="Feedback"
                    />
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => void saveAnswerReview(answer)}
                      sx={{ mt: 1, textTransform: "none" }}
                    >
                      Save Review
                    </Button>
                  </Box>
                )}
              </Box>
              );
            })}
          </Box>
        </Box>

        {/* Video pitch */}
        {videoPitch?.videoUrl && (
          <>
            <Divider sx={{ borderColor: colors.primary[300], mb: 2.5 }} />
            <Box>
              <SectionLabel>Video Pitch ({videoPitch.durationSeconds}s)</SectionLabel>
              <Box sx={{ borderRadius: "12px", overflow: "hidden", backgroundColor: "#0b0b0b", aspectRatio: "16 / 9" }}>
                <video src={videoPitch.videoUrl} controls style={{ width: "100%", height: "100%", objectFit: "cover" }} />
              </Box>
            </Box>
          </>
        )}
      </Box>
    </Drawer>
  );
};

export default CandidateDetailModal;
